import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Company } from '@src/app/core/interfaces/common/company';
import { FormErrorHandlerService } from '@src/app/core/services/helpers/form-error-handler.service';
import { ToastHelperService } from '@src/app/core/services/helpers/toast-helper.service';
import { CompanyService } from '@src/app/core/services/http/company.service';
import { VaultBaseComponent } from '@src/app/shared/components/base/vault-base.component';
import { DialogComponent } from '@src/app/shared/components/dialog/dialog.component';
import { DocumentAttachmentFile } from '@src/app/shared/components/document-attachment/document-attachment.component';
import { SharedModule } from '@src/app/shared/shared.module';

@Component({
  selector: 'app-modal-business-case-upload',
  templateUrl: './company-business-case-upload-modal.component.html',
  styleUrls: ['./company-business-case-upload-modal.component.scss'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, SharedModule],
})
export class ModalBusinessCaseUploadComponent extends VaultBaseComponent {
  @ViewChild('dialog') dialogRef: DialogComponent | null = null;

  @Output() uploadCompleted = new EventEmitter<void>();

  // Component properties
  company: Company | null = null;
  documents: DocumentAttachmentFile[] = [];
  hasExistingFile: boolean = false;

  constructor(
    translateService: TranslateService,
    private companyService: CompanyService,
    private formErrorHandler: FormErrorHandlerService,
    private toastHelper: ToastHelperService
  ) {
    super(translateService);
  }

  async open(companyId: string) {
    this.isLoading = true;
    this.documents = [];
    this.hasExistingFile = false;

    try {
      // Get company information
      this.companyService.getCompanyById(companyId).subscribe({
        next: company => {
          this.company = company;
          this.hasExistingFile = !!company?.businessCaseFilePath;
          this.isLoading = false;

          this.dialogRef?.open(
            '',
            '',
            '',
            'Cancelar',
            '',
            'modal-business-case-upload',
            false,
            true
          );
        },
        error: error => {
          console.error('Error loading company:', error);
          this.formErrorHandler.showToast(error, 'Erro ao carregar informações da empresa');
          this.isLoading = false;
        },
      });
    } catch (error) {
      console.error('Error opening modal:', error);
      this.isLoading = false;
    }
  }

  isFormValid(): boolean {
    return this.documents.length > 0 && this.documents[0].file.size > 0;
  }

  onFileSelected(files: File[]) {
    if (files.length > 0) {
      const file = files[0];
      this.documents = [
        {
          file: file,
          displayName: file.name,
          downloadLink: null,
          id: null,
          errorMessage: null,
          isInvalid: false,
          isValid: true,
          tooltipMessage: null,
        },
      ];
    }
  }

  onRemoveFile(file: DocumentAttachmentFile) {
    this.documents = this.documents.filter(doc => doc !== file);
  }

  downloadCurrentFile() {
    if (this.company?.businessCaseFilePath) {
      this.companyService.getBusinessCaseDownloadUrl(this.company.id!).subscribe({
        next: downloadUrl => {
          if (downloadUrl) {
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.target = '_blank';
            link.download = 'business-case.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        },
        error: error => {
          console.error('Error getting download URL:', error);
          this.toastHelper.showToastError('Erro ao fazer download do arquivo atual');
        },
      });
    }
  }

  submitUpload() {
    if (!this.isFormValid() || !this.company) {
      this.toastHelper.showToastError('Selecione um arquivo para fazer upload');
      return;
    }

    this.isLoading = true;
    const file = this.documents[0].file;
    console.log('file ', file);

    this.companyService.uploadBusinessCase(this.company.id!, file).subscribe({
      next: () => {
        this.toastHelper.showToastSuccess('Business case enviado com sucesso');
        this.uploadCompleted.emit();
        this.dialogRef?.close();
        this.isLoading = false;
      },
      error: error => {
        console.error('Error uploading business case:', error);
        this.formErrorHandler.showToast(error, 'Erro ao fazer upload do business case');
        this.isLoading = false;
      },
    });
  }
}
